import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  decodeFundTypeValue,
  FundTypeSelect,
  fundTypeLabel,
  type BuiltInFundType,
} from '@/components/shared/fund-type-select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox, type ListboxOption, type ListboxSize } from '@/components/ui/listbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';

interface Fund {
  id: string;
  name: string;
  code: string;
  fundType: string;
  customTypeId?: string | null;
  customTypeName?: string | null;
  defaultCurrency: string;
}

interface FundListResponse {
  data: Fund[];
  page?: { totalItems: number };
}

const CREATE_VALUE = '__create_fund__';
const ALL_FUNDS_VALUE = '__all_funds__';
const QUERY_DEBOUNCE_MS = 200;
const CATALOG_PAGE_SIZE = 100;
const SEARCH_PAGE_SIZE = 20;

const codeFromName = (name: string): string => {
  const code = name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 40);
  return code || 'FUND';
};

const fundListPath = (fundTypeFilter?: FundTypeFilter) => {
  const params = new URLSearchParams({
    pageSize: String(CATALOG_PAGE_SIZE),
    activeOnly: 'true',
  });
  if (fundTypeFilter) params.set('fundType', fundTypeFilter);
  return `/giving/funds?${params.toString()}`;
};

const fundSearchPath = (fundTypeFilter: FundTypeFilter | undefined, query: string) => {
  const params = new URLSearchParams({
    pageSize: String(SEARCH_PAGE_SIZE),
    activeOnly: 'true',
    q: query.trim(),
  });
  if (fundTypeFilter) params.set('fundType', fundTypeFilter);
  return `/giving/funds?${params.toString()}`;
};

export type FundTypeFilter = BuiltInFundType;
export type FundSelectDataSource = 'giving' | 'registration';

export interface FundSelectProps {
  value: string | null;
  onChange: (fundId: string | null, fund?: Fund | null) => void;
  /** When set, only funds of this type are listed and new funds default to it. */
  fundType?: FundTypeFilter;
  /** `registration` lists only funds finance marked for programs. Defaults to `giving`. */
  dataSource?: FundSelectDataSource;
  /** When false, hides inline fund creation. Defaults to true for giving, false for registration. */
  allowCreate?: boolean;
  /** First option that clears the value — use for filters, not for recording a gift. */
  allOptionLabel?: string;
  popoverPortal?: PopoverPortalTarget;
  disabled?: boolean;
  id?: string;
  size?: ListboxSize;
  className?: string;
  'aria-label'?: string;
  emptyMessage?: string;
}

const toListboxFund = (fund: Fund, t: (key: string, options?: Record<string, string>) => string) => ({
  value: fund.id,
  label: fund.name,
  description: t('form.fundOptionDescription', {
    type: fundTypeLabel(fund, t),
    currency: fund.defaultCurrency,
  }),
  keywords: [fund.defaultCurrency, fund.code],
});

/**
 * Fund picker with an inline "New fund" path so recording a gift never dead-ends
 * on an empty dropdown. Churches with more than 100 funds search through the API.
 */
export const FundSelect = ({
  value,
  onChange,
  fundType: fundTypeFilter,
  dataSource = 'giving',
  allowCreate,
  allOptionLabel,
  popoverPortal = 'body',
  disabled = false,
  id,
  size,
  className,
  'aria-label': ariaLabel,
  emptyMessage,
}: FundSelectProps) => {
  const { t } = useTranslation('giving');
  const { t: tTips } = useTranslation('tips');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const membership = useCurrentMembership();
  const canManageFunds = hasPermission(membership, PERMISSIONS.GIVING_MANAGE_FUNDS);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [typeValue, setTypeValue] = useState<string>(fundTypeFilter ?? 'offering');
  const [pinnedFund, setPinnedFund] = useState<Fund | null>(null);
  const [fundQuery, setFundQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(fundQuery), QUERY_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [fundQuery]);

  useEffect(() => {
    if (!value) {
      setPinnedFund(null);
      return;
    }
  }, [value]);

  const registrationFunds = useQuery({
    queryKey: ['programs', 'registration-funds'],
    queryFn: () => api.get<{ data: Fund[] }>('/programs/registration-funds'),
    enabled: dataSource === 'registration',
  });

  const catalog = useQuery({
    queryKey: ['giving', 'funds', 'picker-catalog', fundTypeFilter ?? 'all'],
    queryFn: () => api.get<FundListResponse>(fundListPath(fundTypeFilter)),
    enabled: dataSource === 'giving',
  });

  const apiSearch = dataSource === 'giving' && (catalog.data?.page?.totalItems ?? 0) > CATALOG_PAGE_SIZE;

  const searchResults = useQuery({
    queryKey: ['giving', 'funds', 'picker-search', fundTypeFilter ?? 'all', debouncedQuery],
    queryFn: () => api.get<FundListResponse>(fundSearchPath(fundTypeFilter, debouncedQuery)),
    enabled: apiSearch && debouncedQuery.trim().length > 0,
    placeholderData: keepPreviousData,
  });

  const selectedFund = useQuery({
    queryKey: ['giving', 'funds', 'picker-selected', value],
    queryFn: () => api.get<Fund>(`/giving/funds/${value}`),
    enabled:
      dataSource === 'giving' &&
      Boolean(value) &&
      catalog.isSuccess &&
      pinnedFund?.id !== value &&
      !catalog.data?.data.some((fund) => fund.id === value),
  });

  const loadedFunds = useMemo(() => {
    if (dataSource === 'registration') return registrationFunds.data?.data ?? [];
    if (!apiSearch) return catalog.data?.data ?? [];
    if (!debouncedQuery.trim()) return [];
    return searchResults.data?.data ?? [];
  }, [
    apiSearch,
    catalog.data?.data,
    dataSource,
    debouncedQuery,
    registrationFunds.data?.data,
    searchResults.data?.data,
  ]);

  useEffect(() => {
    if (!value) return;
    const loaded = loadedFunds.find((row) => row.id === value);
    if (loaded) {
      setPinnedFund(loaded);
      return;
    }
    if (selectedFund.data?.id === value) setPinnedFund(selectedFund.data);
  }, [loadedFunds, selectedFund.data, value]);

  const options = useMemo((): ListboxOption[] => {
    let rows: ListboxOption[] = loadedFunds.map((fund) => toListboxFund(fund, t));
    if (value && pinnedFund?.id === value && !loadedFunds.some((fund) => fund.id === value)) {
      rows = [toListboxFund(pinnedFund, t), ...rows];
    }
    if (allOptionLabel) {
      rows = [{ value: ALL_FUNDS_VALUE, label: allOptionLabel }, ...rows];
    }
    const showCreate =
      (allowCreate ?? (!allOptionLabel && dataSource === 'giving')) && canManageFunds;
    if (!showCreate) return rows;
    return [
      ...rows,
      {
        value: CREATE_VALUE,
        label: t('funds.createInline'),
        description: tTips('giving.funds.createInline'),
        icon: <Plus className="size-4" aria-hidden />,
      },
    ];
  }, [
    allOptionLabel,
    allowCreate,
    canManageFunds,
    dataSource,
    loadedFunds,
    pinnedFund,
    t,
    tTips,
    value,
  ]);

  const create = useMutation({
    mutationFn: () => {
      const selected = fundTypeFilter
        ? { fundType: fundTypeFilter, customTypeId: null as string | null }
        : decodeFundTypeValue(typeValue);
      return api.post<Fund>('/giving/funds', {
        name: name.trim(),
        code: codeFromName(name),
        fundType: selected.fundType,
        customTypeId: selected.customTypeId,
        defaultCurrency: church?.baseCurrency ?? 'GHS',
      });
    },
    onSuccess: async (fund) => {
      toast.success(t('funds.saved'));
      setCreateOpen(false);
      setName('');
      setTypeValue(fundTypeFilter ?? 'offering');
      await queryClient.invalidateQueries({ queryKey: ['giving', 'funds'] });
      setPinnedFund(fund);
      onChange(fund.id, fund);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const fundsLoading =
    dataSource === 'registration'
      ? registrationFunds.isLoading && !registrationFunds.data
      : catalog.isLoading && !catalog.data;

  const searchLoading =
    apiSearch && debouncedQuery.trim().length > 0 && searchResults.isFetching;

  const listEmptyMessage =
    emptyMessage ??
    (apiSearch && !debouncedQuery.trim() ? t('form.fundSearchPrompt') : t('funds.emptyPicker'));

  const searchable =
    dataSource === 'registration'
      ? (registrationFunds.data?.data ?? []).length > 6
      : apiSearch || (catalog.data?.data ?? []).length > 6;

  return (
    <>
      <Listbox
        id={id}
        className={className}
        size={size}
        value={value ?? (allOptionLabel ? ALL_FUNDS_VALUE : value)}
        onChange={(next) => {
          if (next === CREATE_VALUE) {
            setCreateOpen(true);
            return;
          }
          if (next === ALL_FUNDS_VALUE || next == null) {
            onChange(null, null);
            return;
          }
          const fund =
            loadedFunds.find((row) => row.id === next) ??
            (pinnedFund?.id === next ? pinnedFund : null) ??
            (selectedFund.data?.id === next ? selectedFund.data : null);
          if (fund) setPinnedFund(fund);
          onChange(next, fund);
        }}
        options={options}
        searchable={searchable}
        query={apiSearch ? fundQuery : undefined}
        onQueryChange={apiSearch ? setFundQuery : undefined}
        loading={searchLoading}
        searchPlaceholder={t('funds.filters.search')}
        placeholder={t('form.fundPlaceholder')}
        emptyMessage={listEmptyMessage}
        popoverPortal={popoverPortal}
        disabled={disabled || fundsLoading}
        aria-label={ariaLabel}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('funds.create')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              // Portalled dialogs still bubble through the React tree — without this,
              // saving a fund also submits the surrounding record form.
              event.preventDefault();
              event.stopPropagation();
              if (!name.trim()) return;
              create.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="inline-fund-name">{t('funds.name')}</Label>
              <Input
                id="inline-fund-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                autoFocus
              />
            </div>
            {fundTypeFilter ? null : (
              <div className="flex flex-col gap-1.5">
                <Label>{t('funds.type')}</Label>
                <FundTypeSelect
                  value={typeValue}
                  onChange={setTypeValue}
                  popoverPortal="dialog"
                />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
                {t('funds.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
